// MCS Irradiance Datasets: postcode to zone resolution and the Kk lookup.
//
// Kk is read straight from the MCS workbook (kkTables.generated.ts) at the
// resolution the standard prescribes: inclination rounded to the nearest
// degree, orientation from south rounded to the nearest 5° (MIS 3002
// Appendix B). No interpolation.
import {
  KK_ORIENTATION_COUNT,
  KK_ORIENTATION_STEP,
  KK_PITCH_MAX,
  KK_ZONE_GRIDS_BASE64,
  KK_ZONE_NAMES,
} from './solarKkTables.generated';
import { POSTCODE_OVERRIDES, POSTCODE_ZONES } from './solarMcsZones';

function parsePostcode(
  postcode: string
): { area: string; district: number | null } | null {
  const clean = postcode.toUpperCase().trim();
  if (clean.length < 2) return null;
  let outcode: string;
  if (clean.includes(' ')) {
    outcode = clean.split(' ')[0];
  } else if (clean.length >= 5) {
    outcode = clean.slice(0, -3);
  } else {
    outcode = clean;
  }
  const areaMatch = outcode.match(/^([A-Z]{1,2})/);
  if (!areaMatch) return null;
  const area = areaMatch[1];
  const rest = outcode.slice(area.length);
  const districtMatch = rest.match(/^(\d+)/);
  const district = districtMatch ? parseInt(districtMatch[1], 10) : null;
  return { area, district };
}

/** MCS postcode zone id (e.g. "5E") for a UK postcode, null when unknown. */
export function resolvePostcodeToZone(postcode: string): string | null {
  const parsed = parsePostcode(postcode);
  if (!parsed) return null;
  const { area, district } = parsed;
  if (district !== null && POSTCODE_OVERRIDES[area]) {
    for (const [start, end, zone] of POSTCODE_OVERRIDES[area]) {
      if (district >= start && district <= end) {
        return zone;
      }
    }
  }
  return POSTCODE_ZONES[area] ?? null;
}

export function getZoneName(zoneId: string): string {
  return KK_ZONE_NAMES[zoneId] ?? zoneId;
}

/** Unpack one zone's base64 uint16 grid into rows of pitch × orientation. */
export const decodeKkGrid = (encoded: string): Uint16Array => {
  const binary = atob(encoded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  const view = new DataView(bytes.buffer);
  const grid = new Uint16Array(bytes.length / 2);
  for (let i = 0; i < grid.length; i++) grid[i] = view.getUint16(i * 2, true);
  return grid;
};

const gridCache = new Map<string, Uint16Array>();
const zoneGrid = (zoneId: string): Uint16Array | null => {
  const cached = gridCache.get(zoneId);
  if (cached) return cached;
  const encoded = KK_ZONE_GRIDS_BASE64[zoneId];
  if (!encoded) return null;
  const grid = decodeKkGrid(encoded);
  gridCache.set(zoneId, grid);
  return grid;
};

/**
 * Kk (kWh per kWp) for a zone at the given inclination (degrees from
 * horizontal) and orientation (degrees from south, 0..180), rounded to the
 * table's steps first as Appendix B requires. Due north reads the 175°
 * column: the nearest tabulated column; the workbook stops at 175°.
 */
export function getKkValue(
  zoneId: string,
  pitch: number,
  orientation: number
): number | null {
  const grid = zoneGrid(zoneId);
  if (!grid) return null;
  const p = Math.round(Math.max(0, Math.min(KK_PITCH_MAX, pitch)));
  const oStep = Math.round(
    Math.max(0, Math.min(180, orientation)) / KK_ORIENTATION_STEP
  );
  const o = Math.min(oStep, KK_ORIENTATION_COUNT - 1);
  const value = grid[p * KK_ORIENTATION_COUNT + o];
  return value === undefined ? null : value;
}

/** Compass azimuth (0 = north, 180 = south) to deviation from south. */
export const orientationFromSouth = (azimuthDegrees: number): number => {
  const normalised = ((azimuthDegrees % 360) + 360) % 360;
  return Math.abs(normalised - 180);
};
